import { forwardRef, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, ChevronDown, GripVertical, Phone, Save, Trash2, UserPlus } from "lucide-react";
import { formatPhoneNumber } from "../lib/phone";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { HuntGroupAgentState } from "../api/huntgroupsApi";
import { STATUS_OPTIONS } from "../lib/destinationTransfer";
import { AgentStatusDot } from "../components/AgentStatusChip";
import { PageHeader } from "../components/layout/PageHeader";
import { ConfirmModal } from "../components/ui/ConfirmModal";
import {
  huntgroupsApi,
  type HuntGroupAgent,
  type HuntGroupAgentSummary,
  type HuntGroupDetail,
} from "../api/huntgroupsApi";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; detail: HuntGroupDetail }
  | { status: "error"; message: string };

const DISTRIBUTION_TYPES: { id: number; label: string }[] = [
  { id: 0, label: "Round-Robin" },
  { id: 1, label: "Top-Down" },
  { id: 2, label: "Load Balanced" },
  { id: 3, label: "Longest Free" },
];

const QUEUE_LIMIT_TYPES: { id: number; label: string }[] = [
  { id: 0, label: "Fixed number of waiting calls" },
  { id: 1, label: "No queue (drop)" },
  { id: 2, label: "Per signed-in destination" },
];

/**
 * Huntgroup priority zones — colour palette per priority. Priority 1 is
 * "first to receive a call" so we lean on the green portal accent
 * colour, then taper down to softer hues as priority gets lower.
 * Priority 0 is the unassigned pool and uses a neutral surface colour.
 */
const PRIO_PALETTE: Record<
  number,
  {
    title: string;
    subtitle: string;
    accent: string;
    accentSoft: string;
    accentDarker: string;
  }
> = {
  1: {
    title: "Priority 1",
    subtitle: "First choice",
    accent: "var(--portal-primary)",
    accentSoft: "var(--portal-primary-soft)",
    accentDarker: "var(--portal-primary-darker)",
  },
  2: {
    title: "Priority 2",
    subtitle: "Second choice",
    accent: "#0ea5b5",
    accentSoft: "#dff7f9",
    accentDarker: "#055c66",
  },
  3: {
    title: "Priority 3",
    subtitle: "Third choice",
    accent: "#7c5cff",
    accentSoft: "#eee9ff",
    accentDarker: "#3a26a3",
  },
  4: {
    title: "Priority 4",
    subtitle: "Last choice",
    accent: "#f59e0b",
    accentSoft: "#fff4e0",
    accentDarker: "#92400e",
  },
};

/**
 * Drop zone identifiers for dnd-kit. The pool uses prio 0; assigned
 * priorities are 1..4. Sortable item IDs encode the agentId so items can
 * coexist in the SortableContext for each column.
 */
const POOL_PRIO = 0;
const PRIOS = [1, 2, 3, 4] as const;

/**
 * A destination is only rung when the agent is Available (statusId 2) and, for a
 * PBX extension, the phone is registered. Anything else is drawn greyed out so it
 * is clear the huntgroup skips it.
 */
function destinationSkipReason(st: HuntGroupAgentState | undefined): string | null {
  if (!st) return null;
  if (st.statusId !== 2) {
    const label = STATUS_OPTIONS.find((o) => o.id === st.statusId)?.label ?? st.statusName ?? `status ${st.statusId}`;
    return `Skipped: agent is ${label.toLowerCase()}`;
  }
  if (st.pbxEnabled && !st.pbxRegistered) return "Skipped: PBX extension is not registered";
  return null;
}

// undefined when the API did not send the state (older build): then nothing is greyed.
const pickState = (a: Partial<HuntGroupAgentState>): HuntGroupAgentState | undefined =>
  typeof a.statusId === "number"
    ? {
        statusId: a.statusId,
        statusName: a.statusName ?? "",
        pbxEnabled: a.pbxEnabled ?? false,
        pbxRegistered: a.pbxRegistered ?? false,
      }
    : undefined;

const droppableId = (prio: number) => `prio-${prio}`;
const sortableItemId = (agentId: number) => `agent-${agentId}`;
const agentIdFromSortable = (id: string | number) =>
  Number(String(id).replace(/^agent-/, ""));

/**
 * HuntGroup detail/editor — mirrors the legacy view but trimmed to the
 * fields aivrmanager exposes. Destination assignments are managed via
 * four priority drop zones plus an "available" pool, matching the old
 * portal UX but rendered with portal-style cards and drop feedback.
 */
export function HuntGroupDetailPage() {
  const { huntGroupId } = useParams<{ huntGroupId: string }>();
  const navigate = useNavigate();
  const id = Number(huntGroupId);
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [editable, setEditable] = useState<HuntGroupDetail | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    let active = true;
    setState({ status: "loading" });
    huntgroupsApi
      .get(id)
      .then((detail) => {
        if (!active) return;
        setState({ status: "ready", detail });
        setEditable(detail);
      })
      .catch((err: unknown) => {
        if (!active) return;
        const message =
          err instanceof Error ? err.message : "Failed to load huntgroup";
        setState({ status: "error", message });
      });
    return () => {
      active = false;
    };
  }, [id]);

  const isDirty = useMemo(() => {
    if (state.status !== "ready" || !editable) return false;
    const a = state.detail;
    const b = editable;
    return (
      a.hgName !== b.hgName ||
      a.queueDistributionTypeId !== b.queueDistributionTypeId ||
      a.queueLimitTypeId !== b.queueLimitTypeId ||
      a.queueLimit !== b.queueLimit ||
      a.postCallHandle !== b.postCallHandle ||
      a.recordCalls !== b.recordCalls
    );
  }, [state, editable]);

  const onSave = async () => {
    if (!editable) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await huntgroupsApi.update(id, {
        hgName: editable.hgName,
        hgRef: editable.hgRef,
        description: editable.description,
        queueDistributionTypeId: editable.queueDistributionTypeId,
        queueLimitTypeId: editable.queueLimitTypeId,
        queueLimit: editable.queueLimit,
        postCallHandle: editable.postCallHandle,
        recordCalls: editable.recordCalls,
      });
      setState({ status: "ready", detail: saved });
      setEditable(saved);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Save failed";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const performDeleteHuntgroup = async () => {
    setDeleteConfirmOpen(false);
    setDeleting(true);
    setError(null);
    try {
      await huntgroupsApi.remove(id);
      navigate("/huntgroups");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Delete failed";
      setError(message);
      setDeleting(false);
    }
  };

  const refreshFromServer = async () => {
    const next = await huntgroupsApi.get(id);
    setState({ status: "ready", detail: next });
    setEditable(next);
  };

  /**
   * Optimistically apply a local assignment change, then persist it. If the
   * server rejects the call we re-pull the detail to get the canonical
   * order back. This keeps drag-and-drop snappy even on touch where each
   * round-trip is otherwise visible.
   */
  const moveAgentTo = async (
    agentId: number,
    prio: number,
    index: number,
    optimistic: HuntGroupDetail,
  ) => {
    setEditable(optimistic);
    setState({ status: "ready", detail: optimistic });
    try {
      await huntgroupsApi.setAgent(id, { agentId, prio, order: index });
      await refreshFromServer();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Assignment failed");
      await refreshFromServer();
    }
  };

  const removeAgent = async (
    agentId: number,
    optimistic: HuntGroupDetail,
  ) => {
    setEditable(optimistic);
    setState({ status: "ready", detail: optimistic });
    try {
      await huntgroupsApi.removeAgent(id, agentId);
      await refreshFromServer();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Removal failed");
      await refreshFromServer();
    }
  };

  if (state.status === "loading" || !editable) {
    return (
      <>
        <PageHeader title="Huntgroup" />
        <div className="dashboard">
          <div className="tile" style={{ color: "var(--portal-muted)" }}>
            Loading…
          </div>
        </div>
      </>
    );
  }

  if (state.status === "error") {
    return (
      <>
        <PageHeader title="Huntgroup" />
        <div className="dashboard">
          <div
            className="tile"
            style={{
              borderColor: "#e74c3c",
              color: "#c0392b",
              background: "#fdecea",
            }}
          >
            <strong>Something went wrong.</strong>
            <div style={{ marginTop: 6, fontSize: "0.9rem" }}>
              {state.message}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={editable.hgName || "Huntgroup"}
        actions={
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="btn"
              onClick={() => navigate("/huntgroups")}
              style={{
                background: "#ffffff",
                color: "var(--portal-primary)",
                borderColor: "var(--portal-primary)",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <ArrowLeft size={16} />
              Back
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onSave}
              disabled={!isDirty || saving}
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <Save size={16} />
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        }
      />
      <div className="dashboard">
        {error && (
          <div
            className="tile"
            style={{
              borderColor: "#e74c3c",
              color: "#c0392b",
              background: "#fdecea",
            }}
          >
            {error}
          </div>
        )}

        <section className="tile">
          <h3 style={{ marginTop: 0 }}>General</h3>
          {editable.linkedDnis.length > 0 && (
            <LinkedDnisBanner items={editable.linkedDnis} />
          )}
          <FormRow label="Name">
            <input
              type="text"
              value={editable.hgName}
              onChange={(e) =>
                setEditable({ ...editable, hgName: e.target.value })
              }
              style={inputStyle}
            />
          </FormRow>
          <FormRow label="Distribution Type">
            <Select
              value={editable.queueDistributionTypeId}
              options={DISTRIBUTION_TYPES}
              onChange={(v) =>
                setEditable({ ...editable, queueDistributionTypeId: v })
              }
            />
          </FormRow>
          <FormRow label="Post-call Handle (sec)">
            <input
              type="number"
              value={editable.postCallHandle}
              onChange={(e) =>
                setEditable({
                  ...editable,
                  postCallHandle: Number(e.target.value) || 0,
                })
              }
              style={{ ...inputStyle, maxWidth: 180 }}
            />
          </FormRow>
        </section>

        <section className="tile">
          <h3 style={{ marginTop: 0 }}>Queueing</h3>
          {/* Queueing is "off" when queueLimit === 0 — there is no separate
              column for it, so the Enabled checkbox is just a UI shorthand
              that sets queueLimit to 1 (on) or 0 (off). */}
          <FormRow label="Enabled">
            <Toggle
              checked={editable.queueLimit > 0}
              label="Queue incoming calls when all destinations are busy"
              onChange={(on) =>
                setEditable({
                  ...editable,
                  queueLimit: on ? Math.max(1, editable.queueLimit) : 0,
                })
              }
            />
          </FormRow>
          <FormRow label="Limit Type">
            <Select
              value={editable.queueLimitTypeId}
              options={QUEUE_LIMIT_TYPES}
              disabled={editable.queueLimit === 0}
              onChange={(v) =>
                setEditable({ ...editable, queueLimitTypeId: v })
              }
            />
          </FormRow>
          <FormRow label="Limit">
            <input
              type="number"
              min={1}
              value={editable.queueLimit === 0 ? "" : editable.queueLimit}
              disabled={editable.queueLimit === 0}
              placeholder={editable.queueLimit === 0 ? "—" : undefined}
              onChange={(e) => {
                const next = Number(e.target.value);
                setEditable({
                  ...editable,
                  queueLimit:
                    Number.isFinite(next) && next >= 1 ? next : 1,
                });
              }}
              style={{
                ...inputStyle,
                maxWidth: 180,
                ...(editable.queueLimit === 0
                  ? {
                      background: "var(--portal-surface)",
                      color: "var(--portal-muted)",
                      cursor: "not-allowed",
                    }
                  : {}),
              }}
            />
          </FormRow>
        </section>

        <PriorityBoard
          detail={editable}
          onMoveAgent={moveAgentTo}
          onUnassign={removeAgent}
        />

        <section className="tile">
          <h3 style={{ marginTop: 0, color: "#c0392b" }}>Delete</h3>
          <p style={{ color: "var(--portal-muted)" }}>
            Deleting a huntgroup is permanent and cannot be undone.
          </p>
          <button
            type="button"
            className="btn"
            onClick={() => setDeleteConfirmOpen(true)}
            disabled={deleting}
            style={{
              background: "#ffffff",
              color: "#c0392b",
              borderColor: "#e74c3c",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Trash2 size={16} />
            {deleting ? "Deleting…" : "Delete huntgroup"}
          </button>
        </section>
      </div>

      {state.status === "ready" && editable && (
        <ConfirmModal
          open={deleteConfirmOpen}
          onClose={() => {
            if (!deleting) setDeleteConfirmOpen(false);
          }}
          title="Delete huntgroup?"
          confirmLabel="Delete"
          tone="danger"
          busy={deleting}
          onConfirm={() => void performDeleteHuntgroup()}
        >
          Are you sure you want to delete huntgroup &quot;
          {editable.hgName || "—"}&quot;? This cannot be undone.
        </ConfirmModal>
      )}
    </>
  );
}

/**
 * Yellow warning banner shown above the <code>hgName</code> input on the
 * detail page when the huntgroup is currently the routing target for one
 * or more numbers (its name is a DNIS prefix). Mirrors the destination
 * page's banner so the UI feels consistent across both detail screens.
 */
function LinkedDnisBanner({
  items,
}: {
  items: HuntGroupDetail["linkedDnis"];
}) {
  return (
    <div
      id="linked-dnis-banner"
      role="status"
      aria-label="Linked numbers warning"
      style={{
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        padding: "12px 14px",
        marginBottom: 16,
        borderRadius: 8,
        border: "1px solid #f1c40f",
        background: "#fffbe6",
        color: "#7a5c00",
      }}
    >
      <AlertTriangle size={18} aria-hidden style={{ marginTop: 2 }} />
      <div style={{ fontSize: "0.9rem", lineHeight: 1.45 }}>
        <strong>
          Linked to{" "}
          {items.map((it, idx) => (
            <span key={it.dnisId}>
              <Link
                to={`/numbers/${it.dnisId}`}
                style={{ color: "inherit", textDecoration: "underline" }}
              >
                {formatPhoneNumber(it.formattedNumber || it.dnisPresentation) ||
                  it.dnisPresentation}
              </Link>
              {idx < items.length - 1 ? ", " : ""}
            </span>
          ))}
          .
        </strong>{" "}
        Changing the dnis prefix in the name will break call routing for{" "}
        {items.length === 1 ? "this number" : "these numbers"}.
      </div>
    </div>
  );
}

/**
 * Priority board — four priority columns plus an unassigned pool below.
 *
 * Drag-and-drop is powered by **@dnd-kit**, which gives us:
 *   - Mouse, touch (long-press 250ms so vertical scrolling still works),
 *     and keyboard reordering for free.
 *   - Within-column reorder via SortableContext + arrayMove.
 *   - Cross-column move by reading the over-droppable on dragEnd.
 *
 * The server is the source of truth, but we apply each move optimistically
 * to the parent state so even on slow links the card "follows the finger".
 */
function PriorityBoard({
  detail,
  onMoveAgent,
  onUnassign,
}: {
  detail: HuntGroupDetail;
  onMoveAgent: (
    agentId: number,
    prio: number,
    index: number,
    optimistic: HuntGroupDetail,
  ) => void;
  onUnassign: (agentId: number, optimistic: HuntGroupDetail) => void;
}) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    // PointerSensor handles mouse + pen; small distance prevents accidental
    // drags when clicking buttons/icons inside a card.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    // TouchSensor uses a long-press so the page can still be scrolled by
    // dragging in empty space. 250ms feels native (Trello/Linear use ~200).
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const groupedByPrio = useMemo(() => {
    const map = new Map<number, HuntGroupAgent[]>();
    for (const prio of PRIOS) {
      map.set(
        prio,
        detail.assignedAgents
          .filter((a) => a.prio === prio)
          .sort((a, b) => a.order - b.order),
      );
    }
    return map;
  }, [detail.assignedAgents]);

  const lookupById = useMemo(() => {
    const m = new Map<number, { name: string; prio: number; state: HuntGroupAgentState | undefined }>();
    for (const a of detail.assignedAgents) {
      m.set(a.agentId, { name: a.agentName, prio: a.prio, state: pickState(a) });
    }
    for (const a of detail.availableAgents) {
      m.set(a.agentId, { name: a.agentName, prio: POOL_PRIO, state: pickState(a) });
    }
    return m;
  }, [detail.assignedAgents, detail.availableAgents]);

  const findContainerForAgent = (agentId: number) =>
    lookupById.get(agentId)?.prio ?? POOL_PRIO;

  /**
   * Custom collision strategy: prefer the directly-pointed-at element
   * (`pointerWithin`) so reorder-targets inside a column win over the
   * column's own droppable. Fall back to rectangle intersection for the
   * empty-column case.
   */
  const collisionDetection: CollisionDetection = (args) => {
    const pointer = pointerWithin(args);
    if (pointer.length > 0) return pointer;
    return rectIntersection(args);
  };

  const handleDragStart = (e: DragStartEvent) => {
    setActiveId(String(e.active.id));
  };

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;

    const activeAgentId = agentIdFromSortable(active.id);
    const fromPrio = findContainerForAgent(activeAgentId);

    // Resolve the target container. `over.id` may be a sortable item
    // (when the cursor is over another card) or the column's droppable.
    let toPrio: number;
    let overIndex: number | null = null;
    const overIdStr = String(over.id);
    if (overIdStr.startsWith("prio-")) {
      toPrio = Number(overIdStr.slice("prio-".length));
    } else {
      const overAgentId = agentIdFromSortable(overIdStr);
      toPrio = findContainerForAgent(overAgentId);
      const list = toPrio === POOL_PRIO
        ? null
        : groupedByPrio.get(toPrio) ?? [];
      if (list) {
        overIndex = list.findIndex((a) => a.agentId === overAgentId);
      }
    }

    // Pool drops: nothing to reorder — either remove (from prio) or no-op.
    if (toPrio === POOL_PRIO) {
      if (fromPrio === POOL_PRIO) return;
      const optimistic = applyUnassign(detail, activeAgentId);
      onUnassign(activeAgentId, optimistic);
      return;
    }

    const target = groupedByPrio.get(toPrio) ?? [];

    // Within-column reorder.
    if (fromPrio === toPrio) {
      const oldIndex = target.findIndex((a) => a.agentId === activeAgentId);
      const newIndex = overIndex ?? target.length - 1;
      if (oldIndex === -1 || oldIndex === newIndex) return;
      const reordered = arrayMove(target, oldIndex, newIndex);
      const optimistic = applyReorder(detail, toPrio, reordered);
      // Stored procedure expects 1-based order.
      onMoveAgent(activeAgentId, toPrio, newIndex + 1, optimistic);
      return;
    }

    // Cross-column move (also covers pool → prio).
    const insertIndex = overIndex !== null ? overIndex : target.length;
    const optimistic = applyMove(
      detail,
      activeAgentId,
      toPrio,
      insertIndex,
      lookupById.get(activeAgentId)?.name ?? "",
    );
    onMoveAgent(activeAgentId, toPrio, insertIndex + 1, optimistic);
  };

  const activeAgent = activeId
    ? lookupById.get(agentIdFromSortable(activeId))
    : null;

  return (
    <section className="tile">
      <header
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <div>
          <h3 style={{ margin: 0 }}>Assigned destinations</h3>
          <p
            style={{
              margin: "4px 0 0",
              color: "var(--portal-muted)",
              fontSize: "0.85rem",
            }}
          >
            Drag destinations between priority zones to change the call
            order, or reorder within a priority. On touch devices, press
            and hold a card briefly before dragging. Drag back to
            "Available" to remove a destination from the huntgroup. Greyed
            destinations are skipped by the huntgroup: the agent is not
            available or the PBX extension is not registered.
          </p>
        </div>
      </header>

      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
            gap: 12,
          }}
        >
          {PRIOS.map((prio) => (
            <PrioColumn
              key={prio}
              prio={prio}
              agents={groupedByPrio.get(prio) ?? []}
            />
          ))}
        </div>

        <div style={{ marginTop: 16 }}>
          <AvailablePool agents={detail.availableAgents} />
        </div>

        <DragOverlay dropAnimation={null}>
          {activeAgent ? (
            <AgentCardVisual
              name={activeAgent.name}
              state={activeAgent.state}
              accent={
                activeAgent.prio === POOL_PRIO
                  ? "var(--portal-muted)"
                  : PRIO_PALETTE[activeAgent.prio]?.accent ??
                    "var(--portal-muted)"
              }
              dragging
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </section>
  );
}

function PrioColumn({
  prio,
  agents,
}: {
  prio: number;
  agents: HuntGroupAgent[];
}) {
  const palette = PRIO_PALETTE[prio];
  const { setNodeRef, isOver } = useDroppable({ id: droppableId(prio) });
  const itemIds = agents.map((a) => sortableItemId(a.agentId));

  return (
    <div
      ref={setNodeRef}
      style={{
        background: isOver ? palette.accentSoft : "#ffffff",
        border: `2px dashed ${isOver ? palette.accent : "var(--portal-border)"}`,
        borderRadius: 14,
        padding: 12,
        minHeight: 200,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        transition: "background 0.12s ease, border-color 0.12s ease",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "4px 6px",
        }}
      >
        <div>
          <div
            style={{
              fontWeight: 700,
              color: palette.accentDarker,
              fontSize: "0.95rem",
            }}
          >
            {palette.title}
          </div>
          <div
            style={{
              color: "var(--portal-muted)",
              fontSize: "0.75rem",
            }}
          >
            {palette.subtitle}
          </div>
        </div>
        <span
          style={{
            background: palette.accent,
            color: "white",
            borderRadius: 999,
            padding: "2px 8px",
            fontSize: "0.75rem",
            fontWeight: 700,
          }}
        >
          {agents.length}
        </span>
      </header>

      <SortableContext
        items={itemIds}
        strategy={verticalListSortingStrategy}
      >
        {agents.length === 0 ? (
          <EmptyDropHint accent={palette.accent} />
        ) : (
          agents.map((a) => (
            <SortableAgentCard
              key={a.agentId}
              agentId={a.agentId}
              name={a.agentName}
              state={pickState(a)}
              accent={palette.accent}
            />
          ))
        )}
      </SortableContext>
    </div>
  );
}

function EmptyDropHint({ accent }: { accent: string }) {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        color: "var(--portal-muted)",
        fontSize: "0.8rem",
        padding: "20px 8px",
        gap: 6,
        flexDirection: "column",
      }}
    >
      <UserPlus size={20} style={{ color: accent }} />
      Drop a destination here
    </div>
  );
}

function AvailablePool({ agents }: { agents: HuntGroupAgentSummary[] }) {
  const { setNodeRef, isOver } = useDroppable({ id: droppableId(POOL_PRIO) });
  const itemIds = agents.map((a) => sortableItemId(a.agentId));

  return (
    <div
      ref={setNodeRef}
      style={{
        background: isOver ? "var(--portal-surface)" : "#ffffff",
        border: `2px dashed ${isOver ? "var(--portal-muted)" : "var(--portal-border)"}`,
        borderRadius: 14,
        padding: 12,
        transition: "background 0.12s ease, border-color 0.12s ease",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "4px 6px",
          marginBottom: 6,
        }}
      >
        <div>
          <div style={{ fontWeight: 700 }}>Available</div>
          <div style={{ color: "var(--portal-muted)", fontSize: "0.75rem" }}>
            Drag a destination into a priority to assign it.
          </div>
        </div>
        <span
          style={{
            background: "var(--portal-surface)",
            color: "var(--portal-muted)",
            borderRadius: 999,
            padding: "2px 8px",
            fontSize: "0.75rem",
            fontWeight: 700,
            border: "1px solid var(--portal-border)",
          }}
        >
          {agents.length}
        </span>
      </header>

      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        {agents.length === 0 ? (
          <p
            style={{
              color: "var(--portal-muted)",
              margin: "8px 6px",
              fontSize: "0.85rem",
            }}
          >
            All destinations have been assigned.
          </p>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 8,
            }}
          >
            {agents.map((a) => (
              <SortableAgentCard
                key={a.agentId}
                agentId={a.agentId}
                name={a.agentName}
                state={pickState(a)}
                accent="var(--portal-muted)"
              />
            ))}
          </div>
        )}
      </SortableContext>
    </div>
  );
}

function SortableAgentCard({
  agentId,
  name,
  state,
  accent,
}: {
  agentId: number;
  name: string;
  state: HuntGroupAgentState | undefined;
  accent: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableItemId(agentId) });

  // Pointer/touch listeners are attached to the whole card so the entire
  // surface is grabbable; the visible grip is just an affordance.
  return (
    <AgentCardVisual
      ref={setNodeRef}
      name={name}
      state={state}
      accent={accent}
      dragging={isDragging}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        // Hide the original while it's lifted into the DragOverlay.
        opacity: isDragging ? 0.35 : 1,
      }}
      rootProps={{ ...attributes, ...listeners }}
    />
  );
}

interface AgentCardVisualProps {
  name: string;
  state?: HuntGroupAgentState;
  accent: string;
  dragging?: boolean;
  style?: React.CSSProperties;
  rootProps?: React.HTMLAttributes<HTMLDivElement>;
}

const AgentCardVisual = forwardRef<HTMLDivElement, AgentCardVisualProps>(
  function AgentCardVisual(
    { name, state, accent, dragging, style, rootProps },
    ref,
  ) {
    const initial = (name || "?").trim().charAt(0).toUpperCase();
    const skipReason = destinationSkipReason(state);
    const skipped = skipReason != null;
    const statusLabel = state
      ? STATUS_OPTIONS.find((o) => o.id === state.statusId)?.label ?? state.statusName ?? `#${state.statusId}`
      : "";
    // Phone icon: green for a PSTN destination or a registered PBX extension, red for a
    // PBX extension that is not registered (the huntgroup cannot reach it).
    const phoneOk = state ? !state.pbxEnabled || state.pbxRegistered : true;
    const phoneTitle = state
      ? state.pbxEnabled
        ? state.pbxRegistered ? "PBX extension, registered" : "PBX extension, not registered"
        : "PSTN destination"
      : undefined;
    return (
      <div
        ref={ref}
        {...rootProps}
        title={skipReason ?? undefined}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: skipped ? "var(--portal-surface)" : "white",
          border: "1px solid var(--portal-border)",
          borderLeft: `4px solid ${skipped ? "var(--portal-border)" : accent}`,
          color: skipped ? "var(--portal-muted)" : undefined,
          borderRadius: 10,
          padding: "8px 10px",
          userSelect: "none",
          cursor: dragging ? "grabbing" : "grab",
          // Disable native touch-scroll/zoom on the card itself so the
          // TouchSensor's long-press hand-off works reliably on mobile.
          touchAction: "none",
          boxShadow: dragging
            ? "0 12px 24px rgba(15,23,42,0.18)"
            : "0 1px 2px rgba(15,23,42,0.04)",
          ...style,
        }}
      >
        <GripVertical
          size={14}
          aria-hidden
          style={{ color: "var(--portal-muted)", flexShrink: 0 }}
        />
        <div
          aria-hidden
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: skipped ? "var(--portal-border)" : accent,
            color: skipped ? "var(--portal-muted)" : "white",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.8rem",
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {initial}
        </div>
        <div
          style={{
            fontSize: "0.9rem",
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={name}
        >
          {name}
        </div>
        {state && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto", flexShrink: 0 }}>
            <AgentStatusDot statusId={state.statusId} statusName={statusLabel} />
            <Phone
              size={15}
              aria-label={phoneTitle}
              role="img"
              style={{ color: phoneOk ? "var(--portal-status-success)" : "#c0392b", flexShrink: 0 }}
            >
              <title>{phoneTitle}</title>
            </Phone>
          </div>
        )}
      </div>
    );
  },
);

/**
 * Optimistic state helpers — produce a new HuntGroupDetail reflecting a
 * drag move locally so the UI updates immediately, before the server
 * round-trips. The server's response is treated as authoritative.
 */
function applyReorder(
  detail: HuntGroupDetail,
  prio: number,
  reordered: HuntGroupAgent[],
): HuntGroupDetail {
  const others = detail.assignedAgents.filter((a) => a.prio !== prio);
  const renumbered = reordered.map((a, idx) => ({
    ...a,
    prio,
    order: idx + 1,
  }));
  return { ...detail, assignedAgents: [...others, ...renumbered] };
}

function applyMove(
  detail: HuntGroupDetail,
  agentId: number,
  toPrio: number,
  insertIndex: number,
  agentName: string,
): HuntGroupDetail {
  const fromAssigned = detail.assignedAgents.find((a) => a.agentId === agentId);
  const fromAvailable = detail.availableAgents.find(
    (a) => a.agentId === agentId,
  );
  const stripped = {
    assignedAgents: detail.assignedAgents.filter((a) => a.agentId !== agentId),
    availableAgents: detail.availableAgents.filter(
      (a) => a.agentId !== agentId,
    ),
  };
  const targetList = stripped.assignedAgents
    .filter((a) => a.prio === toPrio)
    .sort((a, b) => a.order - b.order);
  const others = stripped.assignedAgents.filter((a) => a.prio !== toPrio);
  const source = fromAssigned ?? fromAvailable;
  const insertedAgent: HuntGroupAgent = {
    agentId,
    agentName: source?.agentName ?? agentName,
    agentRef: source?.agentRef ?? "",
    prio: toPrio,
    order: insertIndex + 1,
    statusId: source?.statusId ?? 2,
    statusName: source?.statusName ?? "",
    pbxEnabled: source?.pbxEnabled ?? false,
    pbxRegistered: source?.pbxRegistered ?? false,
  };
  const clampedIndex = Math.max(0, Math.min(insertIndex, targetList.length));
  const newTarget = [
    ...targetList.slice(0, clampedIndex),
    insertedAgent,
    ...targetList.slice(clampedIndex),
  ].map((a, idx) => ({ ...a, order: idx + 1 }));
  return {
    ...detail,
    assignedAgents: [...others, ...newTarget],
    availableAgents: stripped.availableAgents,
  };
}

function applyUnassign(
  detail: HuntGroupDetail,
  agentId: number,
): HuntGroupDetail {
  const removed = detail.assignedAgents.find((a) => a.agentId === agentId);
  if (!removed) return detail;
  const remaining = detail.assignedAgents.filter((a) => a.agentId !== agentId);
  // Renumber siblings within the donor priority so optimistic state matches
  // what the server will return after spHuntGroupAgent_Set_Remove.
  const sameBucket = remaining
    .filter((a) => a.prio === removed.prio)
    .sort((a, b) => a.order - b.order)
    .map((a, idx) => ({ ...a, order: idx + 1 }));
  const others = remaining.filter((a) => a.prio !== removed.prio);
  const summary: HuntGroupAgentSummary = {
    agentId: removed.agentId,
    agentName: removed.agentName,
    agentRef: removed.agentRef,
    statusId: removed.statusId,
    statusName: removed.statusName,
    pbxEnabled: removed.pbxEnabled,
    pbxRegistered: removed.pbxRegistered,
  };
  return {
    ...detail,
    assignedAgents: [...others, ...sameBucket],
    availableAgents: [...detail.availableAgents, summary].sort((a, b) =>
      a.agentName.localeCompare(b.agentName),
    ),
  };
}

function FormRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "200px 1fr",
        gap: 16,
        alignItems: "center",
        padding: "8px 0",
      }}
    >
      <label style={{ color: "var(--portal-muted)", fontWeight: 600 }}>
        {label}
      </label>
      <div>{children}</div>
    </div>
  );
}

function Select({
  value,
  options,
  onChange,
  disabled,
}: {
  value: number;
  options: { id: number; label: string }[];
  onChange: (next: number) => void;
  disabled?: boolean;
}) {
  return (
    <div style={{ position: "relative", maxWidth: 360 }}>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          ...inputStyle,
          paddingRight: 36,
          appearance: "none",
          ...(disabled
            ? {
                background: "var(--portal-surface)",
                color: "var(--portal-muted)",
                cursor: "not-allowed",
              }
            : {}),
        }}
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown
        size={16}
        style={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
          color: disabled ? "var(--portal-border)" : "var(--portal-muted)",
        }}
      />
    </div>
  );
}

function Toggle({
  checked,
  label,
  onChange,
}: {
  checked: boolean;
  label: string;
  onChange: (next: boolean) => void;
}) {
  return (
    <label
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 12px",
  border: "1px solid var(--portal-border)",
  borderRadius: 6,
  fontSize: "0.95rem",
  fontFamily: "inherit",
  background: "#ffffff",
  maxWidth: 360,
};
